import React, { useState } from 'react';
import dayjs from 'dayjs';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faDownload, faFileCsv, faFileExcel, faFilePdf, faTimes } from '@fortawesome/free-solid-svg-icons';
import { bookingListApi } from '@api/api';
import { AppBookingExportRequestFormatEnum } from '@api/generated';

interface ExportDataModalProps {
  isOpen: boolean;
  onClose: () => void;
  salonId: string;
}

type Format = typeof AppBookingExportRequestFormatEnum[keyof typeof AppBookingExportRequestFormatEnum];

const formatOptions: { value: Format; label: string; icon: any; ext: string; mime: string }[] = [
  { value: 'CSV', label: 'CSV', icon: faFileCsv, ext: 'csv', mime: 'text/csv' },
  { value: 'EXCEL', label: 'Excel', icon: faFileExcel, ext: 'xlsx', mime: 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet' },
  { value: 'PDF', label: 'PDF', icon: faFilePdf, ext: 'pdf', mime: 'application/pdf' },
];

const ExportDataModal: React.FC<ExportDataModalProps> = ({ isOpen, onClose, salonId }) => {
  const [format, setFormat] = useState<Format>('CSV');
  const [dateFrom, setDateFrom] = useState(dayjs().startOf('month').format('YYYY-MM-DD'));
  const [dateTo, setDateTo] = useState(dayjs().endOf('month').format('YYYY-MM-DD'));
  const [exporting, setExporting] = useState(false);
  const [error, setError] = useState<string | null>(null);

  if (!isOpen) return null;

  const handleExport = async () => {
    setExporting(true);
    setError(null);
    try {
      const response = await bookingListApi.exportBookings({
        appBookingExportRequest: {
          format,
          salonId,
          dateFrom,
          dateTo,
        },
      });
      const opt = formatOptions.find(o => o.value === format)!;
      const blob = new Blob([response.data as unknown as BlobPart], { type: opt.mime });
      const url = window.URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = `rezervace-${dateFrom}-${dateTo}.${opt.ext}`;
      document.body.appendChild(a);
      a.click();
      a.remove();
      window.URL.revokeObjectURL(url);
      onClose();
    } catch (e: any) {
      setError(e?.message ?? 'Export se nezdařil');
    } finally {
      setExporting(false);
    }
  };

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
      <div className="bg-white rounded-lg shadow-xl max-w-lg w-full">
        <div className="flex items-center justify-between p-6 border-b border-gray-200">
          <div className="flex items-center">
            <FontAwesomeIcon icon={faDownload} className="text-purple-600 mr-3" />
            <h2 className="text-xl font-semibold text-gray-900">Export rezervací</h2>
          </div>
          <button onClick={onClose} className="text-gray-400 hover:text-gray-600">
            <FontAwesomeIcon icon={faTimes} className="text-xl" />
          </button>
        </div>

        <div className="p-6 space-y-5">
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">Formát</label>
            <div className="grid grid-cols-3 gap-2">
              {formatOptions.map(opt => (
                <button
                  key={opt.value}
                  onClick={() => setFormat(opt.value)}
                  className={`flex items-center justify-center gap-2 px-3 py-2 border rounded-lg text-sm transition-colors ${
                    format === opt.value
                      ? 'border-purple-500 bg-purple-50 text-purple-700'
                      : 'border-gray-300 text-gray-700 hover:border-gray-400'
                  }`}
                >
                  <FontAwesomeIcon icon={opt.icon} />
                  {opt.label}
                </button>
              ))}
            </div>
          </div>

          <div className="grid grid-cols-2 gap-4">
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">Od</label>
              <input
                type="date"
                value={dateFrom}
                onChange={e => setDateFrom(e.target.value)}
                className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-purple-500/20 focus:border-purple-500"
              />
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">Do</label>
              <input
                type="date"
                value={dateTo}
                onChange={e => setDateTo(e.target.value)}
                className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-purple-500/20 focus:border-purple-500"
              />
            </div>
          </div>

          {error && <div className="bg-red-50 border border-red-200 text-red-700 text-sm rounded p-3">{error}</div>}
        </div>

        <div className="flex items-center justify-end gap-3 p-6 border-t border-gray-200">
          <button onClick={onClose} className="px-4 py-2 text-gray-700 bg-gray-100 rounded-lg hover:bg-gray-200">
            Zrušit
          </button>
          <button
            onClick={handleExport}
            disabled={exporting}
            className="px-6 py-2 bg-purple-600 text-white rounded-lg hover:bg-purple-700 disabled:opacity-50 disabled:cursor-not-allowed flex items-center gap-2"
          >
            <FontAwesomeIcon icon={faDownload} />
            {exporting ? 'Exportuji…' : 'Stáhnout'}
          </button>
        </div>
      </div>
    </div>
  );
};

export default ExportDataModal;
